import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  TextField,
  Autocomplete,
  Typography,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  Button,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { ExpandMore, ExpandLess, Close } from '@mui/icons-material';
import BankDetailsForm from './BankDetailsForm';
import useLedgerModal from '../hooks/useLedgerModal';
import HsnCodeAutoComplete from './HsnCodeAutoComplete';
import { getGSTRates, getTaxabilityOptions } from '../constants/StaticData';
import { CreateLedgerFormProps } from '../types';

const CreateLedgerForm = <T,>(props: CreateLedgerFormProps<T>) => {
  const { onClose, cx, restrictedflag, accountId } = props;
  const {
    formData,
    options,
    expandGST,
    errors,
    isCreating,
    existingLedgers,
    groupType,
    backdropOpen,
    isDirty,
    isCompanyUnRegistrationType,
    accountGstDetails,
    companyType,
    taxabilityOptions,
    shouldShowGSTTypeAndCess,
    setExpandGST,
    handleChange,
    handleSubmit,
    maybeForceAutofill,
  } = useLedgerModal({ ...props });

  // State for controlling dropdown open states
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [taxabilityDropdownOpen, setTaxabilityDropdownOpen] = useState(false);
  const [gstRateDropdownOpen, setGstRateDropdownOpen] = useState(false);

  const isBankAccount = accountId === 39;
  const isRestricted = restrictedflag === 1 && formData.isEdit;
  const isLedgerNameRestricted = isRestricted && !isBankAccount;
  

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: 600, md: 800 },
        maxHeight: '90vh',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        outline: 'none',
      }}
    >
      {backdropOpen && (
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={backdropOpen}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: '#fff',
          px: 2,
          py: 1.5,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <Typography id="ledger-modal-title" variant="h6">
          {formData.isEdit ? 'Edit Ledger' : 'Add Ledger'}
        </Typography>
        <IconButton onClick={() => onClose(cx)} sx={{ color: '#fff' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        {groupType === 'bank' && (
          <BankDetailsForm errors={errors} data={formData} handleChange={handleChange} maybeForceAutofill={maybeForceAutofill} />
        )}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          <Autocomplete
            freeSolo
            options={formData.ledgerName?.length >= 2 ? existingLedgers : []}
            inputValue={formData.ledgerName}
            disabled={isLedgerNameRestricted}
            onInputChange={(e, newValue) => {
              if (newValue?.length <= 100) {
                handleChange({ target: { name: 'ledgerName', value: newValue } });
              }
            }}
            groupBy={(option) => 'Existing Ledgers '}
            getOptionLabel={(option) => option}
            renderGroup={(params) => (
              <li key={params.key}>
                <Typography
                  sx={{
                    p: 1,
                    fontWeight: 'bold',
                    color: 'red',
                  }}
                >
                  {params.group}
                </Typography>
                {params.children}
              </li>
            )}
            renderOption={(props, option) => (
              <li {...props} style={{ pointerEvents: 'none', opacity: 0.7 }}>
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="New Ledger Name"
                required
                fullWidth
                size="small"
                margin="dense"
                name="ledgerName"
                error={!!errors.ledgerName}
                helperText={errors.ledgerName}
                disabled={isLedgerNameRestricted}
                inputProps={{
                  ...params.inputProps,
                  maxLength: 50,
                }}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    color: 'black',
                    opacity: 1,
                  },
                }}
              />
            )}
            sx={{ flex: '1 1 200px' }}
          />
          <Autocomplete
            key={'grp' + options.length + formData?.selectedGroup}
            fullWidth
            size="small"
            open={groupDropdownOpen}
            onOpen={() => setGroupDropdownOpen(true)}
            onClose={() => setGroupDropdownOpen(false)}
            onFocus={() => {
              // Automatically open dropdown when user tabs to focus on the field
              if (!groupDropdownOpen) {
                setGroupDropdownOpen(true);
              }
            }}
            value={options?.find((opt) => opt.value === formData?.selectedGroup)}
            onChange={(e, newvalue) =>
              handleChange({
                target: { name: 'selectedGroup', value: String(newvalue?.value) },
              })
            }
            options={options || []}
            getOptionLabel={(opt) => String(opt.label)}
            disabled={formData.isEdit && groupType === 'bank' || isRestricted}
            renderInput={(params) => (
              <TextField
                {...params}
                key={'grp' + options.length + formData?.selectedGroup}
                label="Under Group"
                required
                name="seletedGroup"
                size="small"
                margin="dense"
                error={!!errors?.selectedGroup}
                helperText={errors?.selectedGroup}
                disabled={formData.isEdit && groupType === 'bank' || isRestricted}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    color: 'black',
                    opacity: 1,
                  },
                }}
              />
            )}
            sx={{ flex: '1 1 200px' }}
          />
        </Box>

        {/* GST Details wrapper */}
        {groupType === 'account' && !isCompanyUnRegistrationType && (
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mb: 2,
            }}
          >
            {/* GST Header */}
            <Box
              onClick={() => setExpandGST(!expandGST)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                px: 2,
                py: 1.25,
              }}
            >
              <Typography variant="subtitle1" color="primary">
                GST Details
              </Typography>
              {expandGST ? <ExpandLess /> : <ExpandMore />}
            </Box>

            {/* GST Content */}
            <Collapse in={expandGST} timeout="auto" unmountOnExit>
              <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
                {/* HSN/SAC Code and Taxability row */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  {/* HSN/SAC Code - Half width */}
                  <Box sx={{ flex: '1 1 200px' }}>
                   
                    <HsnCodeAutoComplete
                      value={formData.hsnId}
                      onChange={(newvalue) =>
                        handleChange({
                          target: { name: 'hsnId', value: String(newvalue?.value) },
                        })
                      }
                      error={!!errors.hsnId}
                      helperText={errors.hsnId}
                    />
                  </Box>

                  {/* Taxability - Half width */}
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      open={taxabilityDropdownOpen}
                      onOpen={() => setTaxabilityDropdownOpen(true)}
                      onClose={() => setTaxabilityDropdownOpen(false)}
                      onFocus={() => {
                        // Automatically open dropdown when user tabs to focus on the field
                        if (!taxabilityDropdownOpen) {
                          setTaxabilityDropdownOpen(true);
                        }
                      }}
                      value={taxabilityOptions?.find((opt) => opt.value === formData.taxability) || null}
                      onChange={(e, newvalue) =>
                        handleChange({
                          target: { name: 'taxability', value: String(newvalue?.value || ''), type: 'number' },
                        })
                      }
                      options={taxabilityOptions || []}
                      getOptionLabel={(opt: { label: string; value: number }) => String(opt.label)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Taxability"
                          name="taxability"
                          size="small"
                          margin="dense"
                          error={!!errors.taxability}
                          helperText={errors.taxability}
                          sx={{
                            '& .MuiInputBase-input::placeholder': {
                              color: 'black',
                              opacity: 1,
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>

                {/* GST Rate - Show only when taxability is selected and is 1 or 5 */}
                {(formData.taxability === 1 || formData.taxability === 5) && (
                  <Autocomplete
                    fullWidth
                    size="small"
                    open={gstRateDropdownOpen}
                    onOpen={() => setGstRateDropdownOpen(true)}
                    onClose={() => setGstRateDropdownOpen(false)}
                    onFocus={() => {
                      // Automatically open dropdown when user tabs to focus on the field
                      if (!gstRateDropdownOpen) {
                        setGstRateDropdownOpen(true);
                      }
                    }}
                    value={getGSTRates(companyType || 1)?.find((opt) => opt.id === formData.gstRate) || null}
                    onChange={(e, newvalue) =>
                      handleChange({
                        target: { name: 'gstRate', value: String(newvalue?.id || ''), type: 'number' },
                      })
                    }
                    options={getGSTRates(companyType || 1) || []}
                    getOptionLabel={(opt) => String(opt.rate)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="GST Rate"
                        name="gstRate"
                        size="small"
                        margin="dense"
                        error={!!errors.gstRate}
                        helperText={errors.gstRate}
                        sx={{
                          '& .MuiInputBase-input::placeholder': {
                            color: 'black',
                            opacity: 1,
                          },
                        }}
                      />
                    )}
                    sx={{ mb: 2 }}
                  />
                )}

                {/* Cess row responsive - Show only when taxability is selected and is 1 or 5, AND when shouldShowGSTTypeAndCess is true */}
                {(formData.taxability === 1 || formData.taxability === 5) && shouldShowGSTTypeAndCess && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      alignItems: 'flex-start',
                    }}
                  >
                    {/* Radio group box */}
                    <FormControl
                      component="fieldset"
                      required
                      sx={{
                        border: 1,
                        borderColor: 'black',
                        borderRadius: 1,
                        flex: '1 1 200px',
                        p: 2,
                      }}
                      error={!!errors.cessType}
                    >
                      <FormLabel component="legend" sx={{ color: 'text.primary', fontWeight: 'medium' }}>GST compensation Cess Type</FormLabel>
                      <RadioGroup row value={formData.cessType} name="cessType" onChange={handleChange}>
                        <FormControlLabel 
                          value="Percentage" 
                          control={<Radio size="small" />} 
                          label="Percentage" 
                          sx={{ color: 'text.primary' }}
                        />
                        <FormControlLabel 
                          value="Amount" 
                          control={<Radio size="small" />} 
                          label="Amount" 
                          sx={{ color: 'text.primary' }}
                        />
                      </RadioGroup>
                    </FormControl>

                    {/* Cess input */}
                    <TextField
                      label="Cess"
                      fullWidth
                      size="small"
                      margin="dense"
                      value={formData.cessValue}
                      name="cessValue"
                      onChange={handleChange}
                      error={!!errors.cessValue}
                      helperText={errors.cessValue}
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {formData.cessType === 'Percentage' ? '%' : '₹'}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        flex: '1 1 200px',
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, px: 2, pb: 2 }}>
        <Button
          onClick={() => onClose(cx)}
          variant="outlined"
          fullWidth
          disabled={isCreating}
          sx={{
            flex: '1 1 200px',
            height: 40,
            borderColor: 'primary.main',
            color: 'primary.main',
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={!isDirty || isCreating}
          sx={{
            flex: '1 1 200px',
            height: 40,
            textTransform: 'none',
          }}
        >
          {isCreating ? (
            <>
              <Box component="span" sx={{ display: 'inline-block', animation: 'spin 1s linear infinite', mr: 1 }}>
                ⌛
              </Box>
              Processing...
            </>
          ) : formData.isEdit ? (
            'Update'
          ) : (
            'Create'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateLedgerForm;
